// Calm Counter: light, warm and quiet, saffron accent. The default for tills and the
// back office. Has a dark alternate, used when the device prefers dark (customer app).
import { defineTheme } from './types.js';

export const calmCounter = defineTheme({
  id: 'calm-counter',
  name: 'Calm Counter',
  description: 'Light and calm with a saffron accent. Roomy controls, easy on the eyes all day.',
  look: {
    colorScheme: 'light',
    colors: {
      bg: '#f8f5f0',
      surface: '#ffffff',
      surfaceRaised: '#ffffff',
      surfaceSunken: '#f1ebe2',
      border: '#e6ddd0',
      borderStrong: '#8a7e70',
      text: '#1f1a14',
      textMuted: '#5c5247',
      accent: '#b35500',
      accentHover: '#974700',
      onAccent: '#ffffff',
      accentSoft: '#fdebd3',
      onAccentSoft: '#7a3a00',
      focus: '#1d4ed8',
      success: '#1e7a3c',
      onSuccess: '#ffffff',
      warning: '#b45309',
      onWarning: '#ffffff',
      danger: '#b42318',
      onDanger: '#ffffff',
      info: '#1d5fb8',
      onInfo: '#ffffff',
      veg: '#1e7a3c',
      nonVeg: '#a3290f',
      overlay: '#1f1a1499',
      table: {
        free: { bg: '#e7f4ea', fg: '#14532d' },
        occupied: { bg: '#fdebd3', fg: '#7a3a00' },
        billed: { bg: '#e0ecfb', fg: '#1e3a8a' },
        reserved: { bg: '#efe7f7', fg: '#5b2a86' },
      },
      categories: [
        { bg: '#fde2c4', fg: '#6b3000' },
        { bg: '#d9f0dd', fg: '#14532d' },
        { bg: '#dbe8fb', fg: '#1e3a8a' },
        { bg: '#f6dbe4', fg: '#831843' },
        { bg: '#ece0f7', fg: '#5b2a86' },
        { bg: '#d6f1ef', fg: '#134e4a' },
        { bg: '#f7efc8', fg: '#5c4300' },
        { bg: '#e7e2dc', fg: '#3f3a34' },
      ],
    },
    shadows: {
      sm: '0 1px 2px #1f1a1414',
      md: '0 4px 12px #1f1a141a',
      lg: '0 12px 32px #1f1a1424',
    },
  },
  alternate: {
    colorScheme: 'dark',
    colors: {
      bg: '#17130f',
      surface: '#211c17',
      surfaceRaised: '#2a241e',
      surfaceSunken: '#120f0c',
      border: '#3a322a',
      borderStrong: '#8f8273',
      text: '#f5efe7',
      textMuted: '#c4b8aa',
      accent: '#f59e2c',
      accentHover: '#f8b04f',
      onAccent: '#1f1206',
      accentSoft: '#3d2a14',
      onAccentSoft: '#fcd9a8',
      focus: '#8ab4ff',
      success: '#4ec46f',
      onSuccess: '#0d1f12',
      warning: '#f0b53c',
      onWarning: '#1f1606',
      danger: '#ff7a6b',
      onDanger: '#2a0904',
      info: '#7ab2ff',
      onInfo: '#0a1a33',
      veg: '#4ec46f',
      nonVeg: '#ff7a6b',
      overlay: '#000000b3',
      table: {
        free: { bg: '#163321', fg: '#bdf0cb' },
        occupied: { bg: '#3d2a14', fg: '#fcd9a8' },
        billed: { bg: '#172a4a', fg: '#c4dbff' },
        reserved: { bg: '#2e1f42', fg: '#e3cdfb' },
      },
      categories: [
        { bg: '#4a2a0c', fg: '#fdd9b0' },
        { bg: '#163321', fg: '#bdf0cb' },
        { bg: '#172a4a', fg: '#c4dbff' },
        { bg: '#45182b', fg: '#fbc8dc' },
        { bg: '#2e1f42', fg: '#e3cdfb' },
        { bg: '#123633', fg: '#b8efe9' },
        { bg: '#3d3208', fg: '#f6e7a6' },
        { bg: '#36302a', fg: '#e8e1d9' },
      ],
    },
    shadows: {
      sm: '0 1px 2px #00000066',
      md: '0 4px 12px #00000080',
      lg: '0 12px 32px #00000099',
    },
  },
  typography: {
    fontSans:
      '"Inter", "Segoe UI", system-ui, -apple-system, "Noto Sans", Roboto, "Helvetica Neue", Arial, sans-serif',
    fontNumeric:
      '"Inter", "Segoe UI", system-ui, -apple-system, "Noto Sans", Roboto, "Helvetica Neue", Arial, sans-serif',
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      md: '1rem',
      lg: '1.125rem',
      xl: '1.375rem',
      xxl: '1.75rem',
      display: '2.25rem',
    },
    lineHeight: 1.5,
    weights: { regular: 400, medium: 550, bold: 700 },
  },
  radii: { sm: '6px', md: '10px', lg: '16px', pill: '999px' },
  space: 4,
  touchMin: 48,
  features: {
    itemCodeEntry: false,
    tableStrip: false,
    categoryColours: false,
    density: 'comfortable',
    keyboardShortcuts: false,
  },
});
